import type {
  AdminSocialChannelRequest,
  AdminSocialChannelRequestPage,
} from '@inflip/contracts/admin';
import {
  SOCIAL_CHANNEL_REQUEST_STATUS_LABELS,
  SOCIAL_PLATFORM_LABELS,
  SocialChannelRequestStatus,
} from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DataTable } from '../components/data-table';
import { Button, Chip, EmptyState, ErrorBanner, PageHeader, PromptDialog } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDate } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';
import { useSeenVersions } from '../lib/use-seen-versions';

const PAGE_SIZE = 20;

const STATUS_CLS: Record<SocialChannelRequestStatus, string> = {
  PENDING: 'text-amber-600',
  APPROVED: 'text-emerald-600',
  REJECTED: 'text-rose-600',
};

type DialogState =
  | null
  | { kind: 'approve'; request: AdminSocialChannelRequest }
  | { kind: 'reject'; request: AdminSocialChannelRequest };

/**
 * SNS 수동 인증 요청 — 스레드/SOOP/팬더TV 는 OAuth 가 없어서, 요청된 채널 주소를 직접 열어
 * 본인 채널인지 확인한 뒤 팔로워 수를 입력해 승인한다. 승인하면 커뮤니티 티어에 바로 반영.
 */
export function SnsRequestsPage() {
  const { session } = useAdminAuth();
  const [filter, setFilter] = useListViewState<SocialChannelRequestStatus | ''>(
    'status',
    SocialChannelRequestStatus.PENDING,
  );
  const [page, setPage] = useListViewState('page', 1);
  const [result, setResult] = useState<AdminSocialChannelRequestPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);

  const seen = useSeenVersions();
  const loadedQuery = useRef<string | null>(null);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    // 관리자가 직접 부른 목록(처음·필터·쪽)만 "본 것" — 토큰 갱신으로 다시 불러온 건 아니다
    const queryKey = `${filter}|${page}`;
    const baseline = loadedQuery.current !== queryKey;
    loadedQuery.current = queryKey;
    return api
      .listSocialChannelRequests(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        ...(filter ? { status: filter } : {}),
      })
      .then((res) => {
        seen.observe(
          res.items.map((x) => [x.id, x.updatedAt]),
          baseline,
        );
        setResult(res);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, filter, page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      await load();
      // 사이드바 빨간 점도 바로 다시 센다
      notifyPendingWorkChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // 그사이 다른 어드민·회원이 상태를 바꿔 거절됐을 수 있다 — 지금 상태로 다시 그린다
      setDialog(null);
      await load();
    } finally {
      setPending(false);
    }
  }

  const token = session?.accessToken;

  const columns = useMemo<ColumnDef<AdminSocialChannelRequest>[]>(
    () => [
      {
        id: 'status',
        header: '상태',
        size: 90,
        cell: ({ row }) => (
          <span className={`text-xs font-bold ${STATUS_CLS[row.original.status]}`}>
            {SOCIAL_CHANNEL_REQUEST_STATUS_LABELS[row.original.status]}
          </span>
        ),
      },
      {
        id: 'user',
        header: '회원',
        cell: ({ row }) => (
          <>
            <Link
              to="/users/$userId"
              params={{ userId: row.original.userId }}
              className="text-sm font-semibold hover:underline"
            >
              {row.original.userName}
            </Link>
            <p className="text-xs text-muted-foreground">{row.original.userEmail}</p>
          </>
        ),
      },
      {
        id: 'channel',
        header: '채널',
        cell: ({ row }) => (
          <>
            <p className="text-sm font-semibold">{SOCIAL_PLATFORM_LABELS[row.original.platform]}</p>
            <a
              href={row.original.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-72 truncate text-xs text-brand hover:underline"
            >
              {row.original.url}
            </a>
          </>
        ),
      },
      {
        id: 'result',
        header: '처리 결과',
        cell: ({ row }) => {
          const r = row.original;
          if (r.status === SocialChannelRequestStatus.APPROVED) {
            return (
              <span className="text-xs text-muted-foreground">
                팔로워{' '}
                <span className="font-semibold text-foreground">
                  {(r.followerCount ?? 0).toLocaleString('ko-KR')}
                </span>
              </span>
            );
          }
          if (r.status === SocialChannelRequestStatus.REJECTED) {
            return (
              <span className="max-w-60 truncate text-xs text-muted-foreground">
                {r.rejectReason}
              </span>
            );
          }
          return null;
        },
      },
      {
        id: 'requestedAt',
        header: '요청일',
        size: 110,
        cell: ({ row }) => (
          <span className="text-xs tabular-nums text-muted-foreground">
            {formatDate(row.original.createdAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 140,
        cell: ({ row }) => {
          const r = row.original;
          if (r.status !== SocialChannelRequestStatus.PENDING) return null;
          // 화면을 연 뒤 회원이 다시 요청했거나 새로 들어온 요청 — 확인 전에는 승인·거절을 내지 않는다
          if (seen.isStale(r.id, r.updatedAt)) {
            return (
              <div className="flex items-center justify-end gap-1.5">
                <span className="whitespace-nowrap rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700">
                  {seen.isNew(r.id) ? '새로 들어옴' : '내용 바뀜'}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => seen.acknowledge(r.id, r.updatedAt)}
                >
                  확인했어요
                </Button>
              </div>
            );
          }
          return (
            <div className="flex justify-end gap-1.5">
              <Button
                size="sm"
                disabled={pending}
                onClick={() => setDialog({ kind: 'approve', request: r })}
              >
                승인
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={pending}
                onClick={() => setDialog({ kind: 'reject', request: r })}
              >
                거절
              </Button>
            </div>
          );
        },
      },
    ],
    [pending, seen],
  );

  if (!session || !token) return null;

  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="인플루언서 인증 요청"
        description="스레드·SOOP·팬더TV 는 계정 로그인 인증이 없어요. 채널 주소를 직접 열어 본인 채널인지 확인하고, 팔로워 수를 입력해 승인해 주세요."
      />

      <div className="mb-4 flex flex-wrap gap-1.5">
        <Chip
          active={filter === ''}
          onClick={() => {
            setPage(1);
            setFilter('');
          }}
        >
          전체
        </Chip>
        {Object.entries(SOCIAL_CHANNEL_REQUEST_STATUS_LABELS).map(([k, label]) => (
          <Chip
            key={k}
            active={filter === k}
            onClick={() => {
              setPage(1);
              setFilter(k as SocialChannelRequestStatus);
            }}
          >
            {label}
          </Chip>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}

      {result && rows.length === 0 ? (
        <EmptyState message="해당 상태의 인증 요청이 없어요." />
      ) : (
        <DataTable data={rows} columns={columns} getRowId={(r) => r.id} />
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= result.totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {dialog?.kind === 'approve' && (
        <PromptDialog
          title="인증을 승인할까요?"
          description={
            <>
              {SOCIAL_PLATFORM_LABELS[dialog.request.platform]} · {dialog.request.url}
              <br />
              채널에서 직접 확인한 팔로워 수를 입력해 주세요. 커뮤니티 티어 배지에 그대로 쓰여요.
            </>
          }
          placeholder="팔로워 수 (숫자만)"
          submitLabel="승인"
          busy={pending}
          onSubmit={(value) => {
            const followerCount = Number(value.replace(/[^0-9]/g, ''));
            if (!Number.isInteger(followerCount) || followerCount < 0) {
              setError('팔로워 수는 숫자로 입력해 주세요.');
              return;
            }
            void run(() =>
              api.approveSocialChannelRequest(token, dialog.request.id, {
                followerCount,
                seenUpdatedAt: seen.seenOf(dialog.request.id, dialog.request.updatedAt),
              }),
            );
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'reject' && (
        <PromptDialog
          title="인증을 거절할까요?"
          description="거절 사유는 요청한 회원에게 그대로 보여요."
          placeholder="거절 사유"
          submitLabel="거절"
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() =>
              api.rejectSocialChannelRequest(token, dialog.request.id, {
                reason,
                seenUpdatedAt: seen.seenOf(dialog.request.id, dialog.request.updatedAt),
              }),
            )
          }
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}
